/**
 * Conexión con Firebase.
 *
 * Tres decisiones que vale la pena entender:
 *
 * 1. La app habla con Firestore directamente, sin un servidor propio en el
 *    medio. Lo que protege los datos son las reglas de seguridad
 *    (firestore.rules), no el código de esta app. El servidor propio llega
 *    cuando haya lógica que el cliente no pueda ejecutar: el webhook de
 *    WhatsApp, el cálculo de precios, la optimización de rutas.
 *
 * 2. La persistencia offline la da Firestore, no un sistema propio de colas.
 *    Con `persistentLocalCache`, escribir sin señal funciona igual: el dato
 *    queda en IndexedDB y sube solo cuando vuelve la conexión. Es la razón
 *    por la que capturar clientes en la calle no necesita nada especial.
 *
 * 3. Firestore y Storage se cargan bajo demanda, no al abrir la app. Auth
 *    pesa poco y se necesita de inmediato; Firestore pesa varias veces más y
 *    solo hace falta después de entrar. Por eso `db()` y `storage()` son
 *    asíncronas: mantener la primera carga liviana es un requisito del
 *    sistema, no una optimización opcional (RNF-05).
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const configuracionCompleta = Object.values(config).every(
  (v) => typeof v === 'string' && v.length > 0,
);

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Promise<Firestore> | null = null;
let _storage: Promise<FirebaseStorage> | null = null;

function app(): FirebaseApp {
  if (!_app) _app = initializeApp(config);
  return _app;
}

export function auth(): Auth {
  if (!_auth) _auth = getAuth(app());
  return _auth;
}

/**
 * Que Firestore no abra su conexión antes de que la sesión esté lista.
 *
 * Con la sesión guardada de ayer, el primer arranque tras un rato (o tras un
 * despliegue, cuando la app se vuelve a abrir) tiene un token vencido: si
 * Firestore abre su conexión en ese instante, las primeras escuchas salen sin
 * credencial válida y el servidor las rechaza con «permission-denied». Una
 * escucha rechazada así **no se reintenta**: la pantalla se queda sin foto, sin
 * el nombre de la empresa y sin permisos hasta recargar. Esperar a que Auth
 * termine de restaurar la sesión y renueve el token vencido evita el rechazo.
 * Sin sesión (el catálogo público) no espera nada.
 */
async function sesionLista(): Promise<void> {
  const a = auth();
  try {
    await a.authStateReady();
    await a.currentUser?.getIdToken();
  } catch {
    // Sin señal: se sigue con lo que haya; Firestore trabaja con su caché.
  }
}

/** Renueva el token a la fuerza. Para reintentar una escucha rechazada por un token viejo. */
export async function renovarSesion(): Promise<void> {
  try { await auth().currentUser?.getIdToken(true); } catch { /* sin señal */ }
}

/** `true` si el error de Firestore es del tipo que una sesión recién renovada puede arreglar. */
export function rechazoDeSesion(e: unknown): boolean {
  const codigo = (e as { code?: string } | null)?.code;
  return codigo === 'permission-denied' || codigo === 'unauthenticated';
}

export function db(): Promise<Firestore> {
  if (!_db) {
    _db = Promise.all([import('firebase/firestore'), sesionLista()]).then(([fs]) =>
      fs.initializeFirestore(app(), {
        localCache: fs.persistentLocalCache({
          tabManager: fs.persistentMultipleTabManager(),
        }),
      }),
    );
  }
  return _db;
}

export function storage(): Promise<FirebaseStorage> {
  if (!_storage) {
    _storage = import('firebase/storage').then((s) => s.getStorage(app()));
  }
  return _storage;
}
